import type { FC } from "react";
import { useMemo, useCallback, useEffect, useRef, useState } from "react";

import * as _ from "lodash";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { ListEditModal } from "@similarweb/ui-components";

import categoryService from "app/common/services/categoryService";
import type { ICategory, ECategoryType } from "app/common/services/categoryService.types";
import { swSettings } from "app/common/services/swSettings";
import { AdvancedEditing } from "app/components/customCategoriesWizard/AdvancedEditing";
import { CountryDropdown } from "app/components/customCategoriesWizard/CountryDropdown";
import {
    domainsValidation,
    domainValidator,
    ECustomCategoriesDomainsValidation,
} from "app/components/customCategoriesWizard/validators";
import { translate } from "app/filters/i18nFilter";
import { removeProtocolAndWww } from "app/pages/digital-marketing/utils/url";
import { isModuleSupportSegments } from "app/pages/industry-analysis/common/isModuleSupportSegments";
import { resolveCustomAttributes } from "app/pages/industry-analysis/home/resolveCustomAttributes";
import { hasSegmentsClaim } from "app/pages/segments/config/segmentsConfigHelpers";
import { fetchLinesOfBusinessSegmentsByIds } from "app/pages/segments/querybar/hooks/useLinesOfBusinessQuery";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import type { ICountryObject } from "app/services/CountryService";
import RegexPatterns from "app/services/RegexPatterns";
import {
    createLinesOfBusinessSegmentId,
    isLinesOfBusinessSegment,
    parseLinesOfBusinessSegmentId,
} from "app/services/segments/LinesOfBusinessUtils";
import type { ICustomSegment } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { useWebsitesFavicons } from "app/services/sitesService/hooks/useWebsitesFavicons";
import { allTrackers } from "app/services/track/track";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";
import { AutocompleteWithTabs } from "pro-features/components/AutocompleteWithTabs/AutocompleteWithTabs";
import type { IAsset } from "pro-features/components/AutocompleteWithTabs/types";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useTrack } from "pro-features/components/WithTrack/src/useTrack";

import { MAX_DOMAINS_IN_CATEGORY } from "./custom-categories-max-domains";
import { isSegmentsLimitReached } from "./utils";

const EMPTY_FAVICONS_DATA: Record<string, string> = {};

enum ECustomCategoriesWizardMode {
    NEW,
    EDIT,
}

interface ICustomCategoryDomain {
    name: string;
    valid: boolean;
    favicon?: string;
    type?: EAssetType;
    subName?: string; //  represents the second row on listItem ( in this case it will be the segment name)
    id?: string;
}

const AutocompletePlaceholderContainer = styled(FlexRow)`
    align-items: center;
`;

const StyledPlusIcon = styled(SWReactIcons)`
    margin-right: 9px;
    width: 14px;
    height: 14px;

    path {
        fill: ${colorsPalettes.carbon[400]};
    }
`;

const getErrorMessages = () => ({
    domainsLimitation: translate("customcategories.wizard.error.limitation"),
    domainsFormat: translate("customcategories.wizard.error.format"),
    duplicateName: translate("customcategories.wizard.error.duplicatename"),
    emptyName: translate("customcategories.wizard.error.emptyname"),
    emptyDomainsList: translate("customcategories.wizard.error.emptydomains"),
    duplicateDomain: translate("customcategories.wizard.error.duplicatedomain"),
    duplicatedRemoved: translate("customcategories.wizard.error.duplicateremoved"),
    serverError: translate("custom.category.server.error"),
    specialCharactersNotAllowed: translate("custom.category.name.error"),
});

const createDomain = (name: string, favicon: string, type?, subName?: string, id?: string) => {
    const d = _.trim(removeProtocolAndWww(name.toLowerCase()), " /");
    const valid = domainValidator.test(d) && d.indexOf("/") == -1;
    if (type === EAssetType.Segment) {
        return {
            name: d,
            valid,
            favicon,
            type,
            subName,
            id,
        };
    }
    return {
        name: d,
        valid,
        favicon,
        type,
        id: id ? id : d,
    };
};

export interface ICustomCategoriesWizardReactProps {
    onClose?: VoidFunction;
    placeholder?: string;
    namePlaceholder?: string;
    editorTitle?: string;
    initialCategoryType?: ECategoryType;
    customCategoryId?: string;
    customCategoryName?: string;
    showDeleteButton?: boolean;
    reloadOnDelete?: boolean;
    isCreateMode?: boolean;
    importedEntities?: ICustomCategoryDomain[];
    initialSelectedCountry?: ICountryObject;
    onSave?: ({ ctrl: { categoryName, initialCategoryName, country } }, data: any) => any;
    onDelete?: ({ ctrl: { categoryName, initialCategoryName } }, data: any) => any;
    stayOnPage?: boolean;
}

export const CustomCategoriesWizardReact: FC<ICustomCategoriesWizardReactProps> = ({
    onClose,
    placeholder = "customcategories.wizard.editor.placeholder.bold",
    namePlaceholder = "customcategories.wizard.name.placeholder",
    editorTitle = "customcategories.wizard.editor.title",
    initialCategoryType = null,
    customCategoryId,
    customCategoryName,
    showDeleteButton = true,
    reloadOnDelete = true,
    onSave,
    onDelete,
    stayOnPage,
    isCreateMode,
    importedEntities = [],
    initialSelectedCountry,
}) => {
    const [track] = useTrack();

    const shouldStayOnPage = useRef<boolean>(stayOnPage);

    const [categoryType, setCategoryType] = useState<ECategoryType>(initialCategoryType);
    const [mode, setMode] = useState<ECustomCategoriesWizardMode>(
        customCategoryId || customCategoryName ? ECustomCategoriesWizardMode.EDIT : ECustomCategoriesWizardMode.NEW,
    );
    const [categoryId, setCategoryId] = useState<string>();
    const [categoryName, setCategoryName] = useState<string>("");
    const initialCategoryName = useRef<string>("");
    const [entities, setEntities] = useState<ICustomCategoryDomain[]>(importedEntities);
    const [shouldSyncFavicons, setShouldSyncFavicons] = useState<boolean>(false);

    const domainsWithoutIcons = useMemo(() => {
        if (!shouldSyncFavicons) {
            return [];
        }
        return entities.filter((domain) => !domain.favicon).map((domain) => domain.name);
    }, [shouldSyncFavicons, entities]);

    const { data: faviconsData = EMPTY_FAVICONS_DATA } = useWebsitesFavicons(domainsWithoutIcons, undefined, {
        enabled: domainsWithoutIcons.length > 0 && shouldSyncFavicons,
    });
    const [confirmBeforeLeaving, setConfirmBeforeLeaving] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [isListItemsLoading, setIsListItemsLoading] = useState<boolean>(false);
    const [invalidName, setInvalidName] = useState<string | boolean>(false);
    const [saveTooltipErrorText, setSaveTooltipErrorText] = useState<string | boolean>(false);
    const [listItemsErrorText, setListItemsErrorText] = useState<string | boolean>(false);
    const [invalidDomains, setInvalidDomains] = useState<string | boolean>(false);
    const [duplicateDomains, setDuplicateDomains] = useState<string | boolean>(false);
    const [selectedCountry, setSelectedCountry] = useState<ICountryObject>(initialSelectedCountry);
    const [segmentsIds, setSegmentsIds] = useState<string[]>([]);
    const MAX_SEGMENTS_IN_CATEGORY = swSettings.components.IndustryAnalysis.resources.MaxNumOfSegments;

    const { segmentsLoading, customSegmentsMeta } = useSelector((state) => {
        const {
            segmentsModule: { segmentsLoading, customSegmentsMeta },
        } = state;
        return { segmentsLoading, customSegmentsMeta };
    });

    useEffect(() => {
        let predictate;
        if (customCategoryId || customCategoryName) {
            if (customCategoryName) {
                predictate = { text: customCategoryName };
            }

            if (customCategoryId) {
                predictate = { categoryId: customCategoryId };
            }

            const category = _.find<ICategory>(userCategories, predictate);
            if (category) {
                setSegmentsIds(category.segments);
                setCategoryId(category.categoryId);
                setCategoryName(category.text);
                initialCategoryName.current = category.text;
                const tempDomains = _.map(category.domains, function (name: string) {
                    return { name, valid: true, type: EAssetType.Website, id: name };
                });
                setEntities(tempDomains);
                setCategoryType(category.categoryType ? category.categoryType : undefined);
                const country = CategoryServiceUtils.getIndustryCountry(category);
                setSelectedCountry(country);
            }
        }
    }, []);

    const fetchSegments = async () => {
        if (segmentsIds.length === 0) {
            return;
        }

        if (segmentsLoading || !customSegmentsMeta) {
            setIsListItemsLoading(true);
            return;
        }

        const regularSegments = [];
        const lobSegmentIds = [];

        segmentsIds.forEach((segId) => {
            if (isLinesOfBusinessSegment(segId)) {
                lobSegmentIds.push(segId);
            } else {
                // Handle regular segments (custom segments only, not predefined)
                const [segmentObj] = SegmentsUtils.getSegmentObjectByKey<ICustomSegment>(segId, {
                    segments: customSegmentsMeta.Segments,
                });
                if (segmentObj) {
                    regularSegments.push({
                        name: segmentObj?.domain,
                        favicon: segmentObj?.favicon,
                        type: EAssetType.Segment,
                        id: segmentObj.id,
                        subName: segmentObj.segmentName,
                        valid: true,
                    });
                }
            }
        });

        let lobSegments = [];
        if (lobSegmentIds.length > 0) {
            try {
                const lobResponse = await fetchLinesOfBusinessSegmentsByIds(lobSegmentIds);
                lobSegments = lobResponse.Data.map((lobData) => ({
                    name: lobData.Site,
                    favicon: lobData.Favicon,
                    type: EAssetType.Segment,
                    id: createLinesOfBusinessSegmentId(lobData.Site, lobData.Lob),
                    subName: lobData.LobName,
                    valid: true,
                }));
            } catch (error) {
                Sentry.errorTracker({
                    message: "Failed to fetch LOB segments",
                    e: error,
                    params: {
                        extra: {
                            lobSegmentIds,
                        },
                    },
                });

                // Fallback to parsing if API fails
                lobSegmentIds.forEach((segId) => {
                    const lobSegmentData = parseLinesOfBusinessSegmentId(segId);
                    if (lobSegmentData) {
                        const { domain: siteDomain, businessLineName: lobName } = lobSegmentData;
                        lobSegments.push({
                            name: siteDomain,
                            favicon: null,
                            type: EAssetType.Segment,
                            id: segId,
                            subName: lobName,
                            valid: true,
                        });
                    }
                });
            }
        }

        const allSegments = [...regularSegments, ...lobSegments];
        setEntities([...entities, ...allSegments]);
        setIsListItemsLoading(false);
    };

    useEffect(() => {
        fetchSegments();
    }, [customSegmentsMeta, segmentsIds]);

    // since `mode` could be set only once, it is safe to use the useEffect hook
    useEffect(() => {
        if (mode === ECustomCategoriesWizardMode.NEW) {
            track("Pop Up", "open", "Create New Category");
        }
        if (mode === ECustomCategoriesWizardMode.EDIT) {
            setShouldSyncFavicons(true);
        }
    }, [mode]);

    useEffect(() => {
        if (!shouldSyncFavicons || domainsWithoutIcons.length === 0) {
            return;
        }

        // Wait for favicons to load
        if (!faviconsData || Object.keys(faviconsData).length === 0) {
            return;
        }

        // Update entities with favicons
        const newDomains = entities.map((domain) => {
            return {
                ...domain,
                favicon: domain.favicon || faviconsData[domain.name],
            };
        });
        setEntities(newDomains);
        setShouldSyncFavicons(false);
    }, [shouldSyncFavicons, domainsWithoutIcons, faviconsData, entities]);

    const maxDomains = MAX_DOMAINS_IN_CATEGORY;
    const userCategories = UserCustomCategoryService.getCustomCategories();

    const onSaveCategory = () => {
        if (confirmBeforeLeaving) {
            track("Internal Link", "click", "Industry Analysis/View Category/" + categoryName);
            const customIndustry = UserCustomCategoryService.getCustomCategoryById(categoryId);
            navigateToCategory({ customIndustry });
        } else {
            setInvalidName(false);
            setInvalidDomains(false);

            const duplicateName = validateCategoryName();
            const emptyName = _.isEmpty(categoryName);

            if (duplicateName || emptyName) {
                setInvalidName(duplicateName);
                setInvalidName(duplicateName ? getErrorMessages().duplicateName : getErrorMessages().emptyName);
                track("Pop Up", "warning", "Create New Category/Error/Required Field");
            }

            domainsVal(true, entities);

            if (duplicateName || emptyName) {
                return;
            } else {
                if (mode == ECustomCategoriesWizardMode.NEW) {
                    track("Pop Up", "click", `Create New Category/Save/${categoryName};${entities.length}`);
                    addCategory();
                } else if (mode == ECustomCategoriesWizardMode.EDIT) {
                    const segmentsLength = getSegmentsLength();
                    const websitesLength = entities.length - segmentsLength;
                    track("Pop Up", "click", `Edit Category/Save/${categoryName};${entities.length}`);

                    updateCategory().then(() => {
                        const editedIndustry = UserCustomCategoryService.getCustomCategoryById(categoryId);
                        allTrackers.dispatchTrackEvent(
                            "Pop Up",
                            "click",
                            `Edit Category/${websitesLength}/${segmentsLength}`,
                            resolveCustomAttributes(editedIndustry),
                        );
                    });
                }
            }
        }
    };

    const domainsVal = (withTracking: boolean, domains) => {
        const validationResult = domainsValidation(domains, maxDomains);
        switch (validationResult) {
            case ECustomCategoriesDomainsValidation.INVALID_LENGTH:
                setListItemsErrorText(getErrorMessages().domainsLimitation);
                withTracking && track("Pop Up", "warning", `Create New Category/Error/Over ${maxDomains} websites`);
                return;
            case ECustomCategoriesDomainsValidation.INVALID_FORMAT:
                setListItemsErrorText(getErrorMessages().domainsFormat);
                withTracking && track("Pop Up", "warning", "Create New Category/Error/Invalid Format");
                return;
            // TODO: after we will have the duplicated case design then we will handle it here:
            // case ECustomCategoriesDomainsValidation.DOMAIN_DUPLICATED:
            //     setInvalidDomains(errorMessages.duplicateDomain);
            //     setListItemsErrorText(false);
            //     withTracking &&
            //         track("Pop Up", "warning", "Create New Category/Error/Duplicated domains");
            //     return;
            default:
                setInvalidDomains(false);
                setListItemsErrorText(false);
                return;
        }
    };

    const navigateToCategory = ({ customIndustry, toState }: { customIndustry: ICategory; toState?: string }) => {
        const gotoPage = confirmBeforeLeaving
            ? "digitalsuite_marketresearch_webmarketanalysis_overview"
            : proNavigator.current().name;
        const currentParams = proNavigator.getParams();
        const industryCountry = CategoryServiceUtils.getIndustryCountry(customIndustry);

        setLoading(true);
        void proNavigator.go(
            toState ?? gotoPage,
            {
                ...currentParams,
                category: customIndustry.forUrl,
                country: industryCountry?.id ?? currentParams.country,
            },
            { reload: true },
        );
        onClose?.();
    };

    const validateCategoryName = () => {
        // if the user didn't change the category name
        if (categoryName == initialCategoryName.current) {
            return false;
        } else {
            return _.some(userCategories, { name: categoryName.trim() });
        }
    };

    const onInputChange = (e) => {
        // track if the user start to input a group name
        if (categoryName.length === 1) {
            allTrackers.dispatchTrackEvent("Pop Up", "value-ok", "Create New Website Group");
        }

        const newTitle = e.target.value;
        setCategoryName(newTitle);

        if (newTitle.length === 0) {
            setInvalidName(""); // if title is empty we will set the error message to empty string so we wont see the text but the color will become red.
            setSaveTooltipErrorText(getErrorMessages().emptyName);
            return;
        }
        if (RegexPatterns.testCustomCategoryNameUnicode(newTitle)) {
            setInvalidName(getErrorMessages().specialCharactersNotAllowed);
            setSaveTooltipErrorText(getErrorMessages().specialCharactersNotAllowed);
            return;
        }

        setInvalidName(false);
        setSaveTooltipErrorText(false);
    };

    const removeDomain = (index: number, $event: any) => {
        $event.stopPropagation();
        const newDomains = [...entities];
        const domain: any = newDomains.splice(index, 1);
        if (mode == ECustomCategoriesWizardMode.NEW) {
            track("Pop Up", "click", `Create New Category/Remove Domain/${domain.name}`);
        } else if (mode == ECustomCategoriesWizardMode.EDIT) {
            track("Pop Up", "click", `Edit Category/Remove Domain/${domain.name}`);
        }
        setEntities(newDomains);
        domainsVal(false, newDomains);
    };

    const isDomainAlreadyExists = (domain: string, type) => {
        return entities.find((d) => d.name === domain && d.type === type);
    };
    const addDomain = (entity: IAsset) => {
        const { type = EAssetType.Website, name = "", id = "", displayText, image } = entity;

        if (isDomainAlreadyExists(name, type)) {
            setSaveTooltipErrorText(getErrorMessages().duplicateDomain);
            return;
        }
        const newDomains: ICustomCategoryDomain[] = [createDomain(displayText, image, type, name, id), ...entities];
        setEntities(newDomains);

        if (type === EAssetType.Website) {
            track("Pop Up", "click", `Edit Category/Add Domain/${displayText}`);
        } else {
            track("Pop Up", "click", `Edit Category/Add Segment/${displayText} (${name}), ${id}`);
        }
        domainsVal(false, newDomains);
    };

    const addCategory = async () => {
        setLoading(true);
        try {
            const data = await UserCustomCategoryService.addCustomCategory({
                name: categoryName,
                domains: entities.filter((domain) => domain?.type !== EAssetType.Segment).map((domain) => domain.name),
                segments: entities.filter((domain) => domain?.type === EAssetType.Segment).map((domain) => domain.id),
                CategoryType: categoryType,
                ...(selectedCountry && { country: selectedCountry.id }),
            });

            //If onSave parameter was passed trigger it
            if (typeof onSave === "function") {
                // TODO: remove ctrl when available
                onSave(
                    {
                        ctrl: {
                            categoryName,
                            initialCategoryName: initialCategoryName.current,
                            country: selectedCountry,
                        },
                    },
                    data,
                );
            }

            if (!shouldStayOnPage.current) {
                const customIndustry = UserCustomCategoryService.getCustomCategoryByName(categoryName);
                navigateToCategory({ customIndustry });
            }
        } catch (error) {
            setInvalidName(getErrorMessages().serverError);
            setLoading(false);
        }
    };

    const updateCategory = (): Promise<unknown> => {
        const update = UserCustomCategoryService.updateCustomCategory({
            name: categoryName,
            domains: entities.filter((domain) => domain?.type !== EAssetType.Segment).map((domain) => domain.name),
            segments: entities.filter((domain) => domain?.type === EAssetType.Segment).map((domain) => domain.id),
            id: categoryId,
            CategoryType: categoryType,
            ...(selectedCountry && { country: selectedCountry.id }),
        });
        setLoading(true);
        update.then(
            (updatedCustomCategories) => {
                //If onSave parameter was passed trigger it
                if (typeof onSave === "function") {
                    // TODO: remove ctrl when avaiable
                    onSave(
                        {
                            ctrl: {
                                categoryName,
                                initialCategoryName: initialCategoryName.current,
                                country: selectedCountry,
                            },
                        },
                        updatedCustomCategories,
                    );
                }
                if (!shouldStayOnPage.current) {
                    const customIndustry = UserCustomCategoryService.getCustomCategoryById(categoryId);
                    navigateToCategory({ customIndustry });
                }
            },
            function (error) {
                setInvalidName(getErrorMessages().serverError);
                setLoading(false);
            },
        );
        return update;
    };

    const deleteCategory = () => {
        setLoading(true);
        let currentCategory;
        const currentState = proNavigator.current();
        const currentStateParams = proNavigator.getParams();
        currentCategory = currentStateParams.category
            ? categoryService.categoryQueryParamToCategoryObject(currentStateParams.category)
            : null;
        if (currentStateParams.partnerListId) {
            currentCategory = _.find(UserCustomCategoryService.getCustomCategories(), {
                categoryId: currentStateParams.partnerListId,
            });
        }
        // Run delete action via the user service
        const deleteAction = UserCustomCategoryService.deleteCustomCategory({ id: categoryId });
        deleteAction.then(
            (updatedCustomCategories) => {
                //If onDelete parameter was passed trigger it
                if (typeof onDelete === "function") {
                    onDelete({ ctrl: { categoryName, initialCategoryName } }, updatedCustomCategories);
                }
                // Once the category has been deleted - check if the current page has this category selected
                // if so - redirect to the current state homepage. otherwise - refresh the page, so that
                // the category will not appear in the lists anymore.
                const shouldRedirectToHomeState = currentCategory?.categoryId === categoryId;
                const targetState = shouldRedirectToHomeState ? currentState.homeState : currentState;
                setLoading(false);
                onClose?.();
                proNavigator.go(targetState, shouldRedirectToHomeState ? {} : currentStateParams, {
                    reload: reloadOnDelete,
                });
            },
            (err) => {
                // In case of an error - close the modal.

                setLoading(false);
                onClose?.();
            },
        );
        track("Pop Up", "open", "Delete custom industry/Edit modal deletion");
    };

    const onPaste = (event: ClipboardEvent) => {
        const pasted = event.clipboardData.getData("text").trim();
        if (pasted) {
            const tmpDomains = _.filter(pasted.replace(/[\r\n]/g, ",").split(","));
            if (tmpDomains.length > 0) {
                // check for duplications
                const pastedDomains = [];
                _.forEach(tmpDomains, (name: string) => {
                    pastedDomains.push(createDomain(name, ""));
                });
                const all = _.concat(pastedDomains, entities);
                const uniques = _.uniqWith(all, (crr, other) => crr.name === other.name);
                if (all.length != uniques.length && uniques.length <= maxDomains) {
                    setDuplicateDomains(`${all.length - uniques.length} ${getErrorMessages().duplicatedRemoved}`);
                } else {
                    setDuplicateDomains(false);
                }
                setEntities(uniques);

                if (mode == ECustomCategoriesWizardMode.NEW) {
                    track("Pop Up", "click", `Create New Category/Paste Domains/${tmpDomains.length}`);
                } else if (mode == ECustomCategoriesWizardMode.EDIT) {
                    track("Pop Up", "click", `Edit Category/Paste Domains/${tmpDomains.length}`);
                }
                event.preventDefault();
                domainsVal(false, uniques);
            }
        }
    };

    const deleteButtonText = "customcategories.wizard.delete.button";
    const deleteButtonTextLoading = "customcategories.wizard.delete.button.loading";
    let saveButtonText;
    let saveButtonTextLoading;
    if (mode === ECustomCategoriesWizardMode.NEW) {
        saveButtonText = "customcategories.wizard.save.button";
        saveButtonTextLoading = "customcategories.wizard.save.button.loading";
    } else {
        saveButtonText = "customcategories.wizard.edit.button";
        saveButtonTextLoading = "customcategories.wizard.edit.button.loading";
    }

    const autoCompleteExcludes: IAsset[] = entities.map((domain) => {
        return {
            displayText: domain.name,
            name: domain.subName,
            type: domain.type,
            id: domain.id,
            image: domain.favicon,
        };
    });

    const segmentLimitCheck = useCallback(
        () => isSegmentsLimitReached(entities, MAX_SEGMENTS_IN_CATEGORY),
        [MAX_SEGMENTS_IN_CATEGORY, entities],
    );

    const showCustomTabsList = categoryService.hasAdvancedCustomIndustriesPermission() && isModuleSupportSegments();

    const tabsProps = {
        showAccountSegments: false,
        disabledTabText: translate("market.builder.domains.autocomplete.segments.limit"),
        scrollMaxHeight: 220,
        showCustomTabsList,
    };

    const GetAutocompleteComp = () => {
        return (
            <AutocompleteWithTabs
                key={entities.length}
                shouldDisableSegmentsTab={segmentLimitCheck}
                autocompleteProps={{
                    onPaste,
                    searchIcon: null,
                    placeholder: (
                        <AutocompletePlaceholderContainer>
                            <StyledPlusIcon iconName="plus" />
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: translate(placeholder),
                                }}
                            />
                        </AutocompletePlaceholderContainer>
                    ),
                    top: "0",
                    width: "400",
                    position: "static",
                    hideBorder: true,
                }}
                onSelect={addDomain}
                excludeItems={autoCompleteExcludes}
                isDisabledListItemSupported={true}
                tabsProps={tabsProps}
                hasBorder={false}
                showLinesOfBusiness={true}
            />
        );
    };

    const BottomChildComp = () => {
        const customIndustry = UserCustomCategoryService.getCustomCategoryById(categoryId);
        return (
            mode === ECustomCategoriesWizardMode.EDIT &&
            categoryService.hasAdvancedCustomIndustriesPermission() && (
                <AdvancedEditing
                    customIndustry={customIndustry}
                    navigateToCategory={navigateToCategory}
                />
            )
        );
    };

    const getSegmentsLength = () => {
        let segmentsLength = 0;
        entities.forEach((domain) => {
            if (domain.type === EAssetType.Segment) {
                segmentsLength++;
            }
        });
        return segmentsLength;
    };

    const getEntityCountText = () =>
        `${entities.length} / ${maxDomains} ${translate("customcategories.wizard.editor.limit.entities")}`;

    const getSegmentsCountText = () => {
        if (!hasSegmentsClaim()) {
            return "";
        }
        const segmentsLength = getSegmentsLength();
        if (segmentsLength === 0 && !isModuleSupportSegments()) {
            return "";
        }
        const segmentsText = translate("customcategories.wizard.editor.limit.segments");
        return `| ${segmentsLength} / ${MAX_SEGMENTS_IN_CATEGORY} ${segmentsText}`;
    };

    return (
        <ListEditModal
            listItems={entities}
            onDelete={isCreateMode ? null : deleteCategory}
            onSave={onSaveCategory}
            removeItem={removeDomain}
            onCancel={onClose}
            onTitleInputChange={onInputChange}
            deleteButtonText={translate(loading ? deleteButtonTextLoading : deleteButtonText)}
            saveButtonText={translate(loading ? saveButtonTextLoading : saveButtonText)}
            cancelButtonText={translate("customcategories.wizard.editor.cancel.btn")}
            titlePlaceholder={translate(namePlaceholder)}
            saveTooltipErrorText={translate(saveTooltipErrorText as string)}
            autocompleteComp={GetAutocompleteComp()}
            isLoading={loading}
            isListLoading={isListItemsLoading}
            listLoadingText={translate("customcategories.wizard.editor.list.loading")}
            topLeftHeader={translate(editorTitle)}
            topRightHeader={`${getEntityCountText()} ${getSegmentsCountText()}`}
            inputErrorMessage={translate(invalidName as string) || translate(invalidDomains as string)}
            listItemsErrorMsg={translate(listItemsErrorText as string)}
            isSaveDisabled={
                !!listItemsErrorText || !!invalidName || !!saveTooltipErrorText || entities.length === 0 || loading
            }
            inputTitleText={categoryName}
            className="customCategoriesWizard"
            bottomChildComp={BottomChildComp()}
            isDeleteDisabled={!showDeleteButton || loading}
            scrollAreaHeight={"280px"}
            topChildComp={
                categoryService.hasAdvancedCustomIndustriesPermission() ? (
                    <CountryDropdown
                        selectedCountry={selectedCountry}
                        onChangeSelectedCountry={setSelectedCountry}
                    />
                ) : null
            }
            width={"574px"}
        />
    );
};
